import { FC, useEffect, useState } from 'react';
import MultiStageSearchButton from './MultiStageSearchButton';
import {
  IconSearch,
  IconMinus,
  IconPlus,
  IconMapPinFilled,
  IconCalendarEvent,
  IconArrowLeft,
  IconArrowRight,
} from '@tabler/icons-react';
import MultiStageSearchOption from './MultiStageSearchOption';
import MultiStageSearchMonthOption from './MultiStageSearchMonthOption';
import { MonthData } from '../common/constants/monthData';
import { HomeAction } from '../reducers/homeReducer';
import { buildTokenizedQueryString } from '../common/utilities/encodeURIComponent';
import { useNavigate } from 'react-router-dom';
import useGetBoats from '../hooks/useGetBoats';
import useGetCountries from '../hooks/useGetCountries';
import useGetRoutes from '../hooks/useGetRoutes';
import { SearchType } from '../common/constants/searchType';

export interface SearchData {
  label: string;
  type: number;
  id: number;
}

interface Props {
  setShowSearchOverlay: (showSearchOverlay: boolean) => void;
  diver: number;
  destination: string;
  month: string;
  currentYear: number;
  currentStage: number;
  destinationId: number;
  monthId: number;
  searchType: number;
  dispatch: React.Dispatch<HomeAction>;
  onClose?: () => void;
  showPopOverForce?: boolean;
}

const MultiStageSearch: FC<Props> = ({
  setShowSearchOverlay,
  diver,
  destination,
  month,
  currentYear,
  currentStage,
  destinationId,
  monthId,
  searchType,
  dispatch,
  onClose,
  showPopOverForce,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const navigate = useNavigate();
  const { countries } = useGetCountries();
  const { routes } = useGetRoutes();
  const { boats } = useGetBoats();
  useEffect(() => {
    const currentDate = new Date();
    if (currentYear === 0) {
      dispatch({
        type: 'SET_CURRENT_YEAR',
        payload: currentDate.getFullYear(),
      });
    }
  }, [dispatch, currentYear]);

  useEffect(() => {
    if (showPopOverForce) {
      setShowPopover(showPopOverForce);
    }
  }, [showPopOverForce]);

  const groupDestinationsIntoRows = (data: string[], itemsPerRow: number) => {
    const rows = [];
    for (let i = 0; i < data.length; i += itemsPerRow) {
      rows.push(data.slice(i, i + itemsPerRow));
    }
    return rows;
  };

  const rowsOfMonth = groupDestinationsIntoRows(MonthData, 3);

  const handleWhereButtonClick = () => {
    setShowPopover(true);
    dispatch({ type: 'SET_CURRENT_STAGE', payload: 1 });
  };

  const handleWhenButtonClick = () => {
    setShowPopover(true);
    dispatch({ type: 'SET_CURRENT_STAGE', payload: 2 });
  };

  const handleChangeYear = (year: number) => {
    dispatch({ type: 'SET_CURRENT_YEAR', payload: year });
  };

  const handleDiversButtonClick = () => {
    setShowPopover(true);
    dispatch({ type: 'SET_CURRENT_STAGE', payload: 3 });
  };

  const setDestinationId = (destinationId: number) => {
    dispatch({ type: 'SET_DESTINATION_ID', payload: destinationId });
  };

  const setMonthId = (monthId: number) => {
    dispatch({ type: 'SET_MONTH_ID', payload: monthId });
  };

  const SearchData = (): SearchData[] => {
    const searchData: SearchData[] = [];
    countries?.countries.map(country => {
      searchData.push({
        label: country.countryName,
        type: SearchType.Country,
        id: country.countryId,
      });
    });
    routes?.routes.map(route => {
      searchData.push({
        label: route.routeName,
        type: SearchType.Route,
        id: route.routeId,
      });
    });
    boats?.boats.map(boat => {
      searchData.push({
        label: boat.name,
        type: SearchType.Boat,
        id: boat.boatId,
      });
    });
    return searchData;
  };

  const handleDestinationBoxClick = (
    destinationName: string,
    destinationId: number,
  ) => {
    dispatch({ type: 'SET_DESTINATION', payload: destinationName });
    dispatch({ type: 'SET_SEARCH_TYPE', payload: 1 });
    setDestinationId(destinationId);
    dispatch({ type: 'SET_CURRENT_STAGE', payload: 2 });
  };

  const handleMonthBoxClick = (monthName: string, monthId: number) => {
    dispatch({ type: 'SET_MONTH', payload: monthName });
    setMonthId(monthId);
    dispatch({ type: 'SET_CURRENT_STAGE', payload: 3 });
  };

  const handleAlignItems = () => {
    switch (currentStage) {
      case 1:
        return 'items-start';
      case 2:
        return 'items-center';
      case 3:
        return 'items-end';
      default:
        break;
    }
  };

  const handleSearchClick = () => {
    const queryParams = buildTokenizedQueryString({
      destinationId,
      monthId,
      diver,
      currentYear,
      searchType,
      destination,
      month,
    });

    navigate(`/search?${queryParams}`);
    location.reload();
  };

  const renderDestinationSelector = () => (
    <div className="p-6 flex flex-col w-[47rem] relative bg-white rounded-3xl">
      <div className="font-bold">Destination countries</div>
      <div className="grid grid-cols-5 gap-3">
        {countries?.countries.map((row, index) => (
          <div key={index} className="flex gap-3">
            <MultiStageSearchOption
              key={row.countryId}
              option={row.countryName}
              icon={<IconMapPinFilled />}
              action={() =>
                handleDestinationBoxClick(row.countryName, row.countryId)
              }
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => handleDestinationBoxClick('Anywhere', 0)}
        className="text-center text-brand-santtai-primary font-bold text-lg mt-4"
      >
        I'm flexible, search for anywhere
      </button>
    </div>
  );

  const renderMonthSelector = () => (
    <div className="mt-2 p-6 flex flex-col w-[25rem] relative bg-white rounded-3xl">
      <div className="font-bold flex justify-between text-center">
        {currentYear > new Date().getFullYear() ? (
          <button
            type="button"
            onClick={() => handleChangeYear(currentYear - 1)}
          >
            <IconArrowLeft />
          </button>
        ) : (
          <div className="w-6" />
        )}
        <div>{currentYear}</div>
        <button type="button" onClick={() => handleChangeYear(currentYear + 1)}>
          <IconArrowRight />
        </button>
      </div>
      {rowsOfMonth.map((row, index) => (
        <div key={index} className="flex gap-3">
          {row.map((moth: string) => (
            <MultiStageSearchMonthOption
              key={moth}
              option={moth}
              icon={<IconCalendarEvent />}
              action={() =>
                handleMonthBoxClick(moth, MonthData.indexOf(moth) + 1)
              }
              currentYear={currentYear}
            />
          ))}
        </div>
      ))}
      <button
        type="button"
        onClick={() => handleMonthBoxClick('Anytime', 0)}
        className="text-center text-brand-santtai-primary font-bold text-lg mt-4"
      >
        I'm flexible, search for anytime
      </button>
    </div>
  );

  const renderDiverSelector = () => (
    <div className="mt-2 p-6 flex flex-col w-50 items-end relative bg-white rounded-[2rem] absolute">
      <div className="font-bold flex justify-between items-center p-3 h-8">
        <div>Divers</div>
        <div className="ml-4 flex justify-center items-center">
          <button
            className={`p-2 border border-solid ${
              diver > 1
                ? 'border-neutral-100 text-black'
                : 'border-neutral-50 text-neutral-50'
            } rounded-full`}
            onClick={() => dispatch({ type: 'SET_DIVER', payload: diver - 1 })}
            disabled={diver <= 1}
          >
            <IconMinus />
          </button>
          <span className="mx-2">{diver}</span>
          <button
            onClick={() => dispatch({ type: 'SET_DIVER', payload: diver + 1 })}
            className="p-2 border border-solid border-neutral-100 rounded-full"
          >
            <IconPlus />
          </button>
        </div>
      </div>
    </div>
  );
  return (
    <div
      className={`mt-6 flex flex-col justify-center lg:mt-10 ${handleAlignItems()}`}
    >
      {showPopover && (
        <div
          className="fixed top-0 left-0 z-40 h-screen w-full bg-black bg-opacity-50 invisible lg:visible"
          onClick={() => {
            setShowPopover(false);
            if (onClose) {
              onClose();
            }
          }}
        />
      )}
      <div
        className={`rounded-full items-center hidden w-[22rem] h-16 ${
          showPopover ? 'bg-neutral-100' : 'bg-white'
        } relative z-50
        lg:w-[47rem] lg:flex`}
      >
        <MultiStageSearchButton
          title="Where"
          value={destination}
          action={() => handleWhereButtonClick()}
          isFocused={showPopover && currentStage === 1}
          content={renderDestinationSelector()}
          dispatch={dispatch}
          autoCompleteValueOption={SearchData()}
        />
        <MultiStageSearchButton
          title="When"
          value={month}
          action={() => handleWhenButtonClick()}
          isFocused={showPopover && currentStage === 2}
          content={renderMonthSelector()}
        />
        <MultiStageSearchButton
          title="Divers"
          value={diver > 1 ? `${diver} Divers` : `${diver} Diver`}
          action={() => handleDiversButtonClick()}
          isFocused={showPopover && currentStage === 3}
          isLastElement
          content={renderDiverSelector()}
        />
        <div className="w-32 flex justify-end absolute right-0">
          {showPopover ? (
            <button
              className={`m-2 w-28 h-14 text-white flex justify-center items-center rounded-full bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker`}
              type="button"
              onClick={() => handleSearchClick()}
              aria-label="Search"
            >
              {showPopover ? <span className="mr-2">Search</span> : <div />}
              <IconSearch />
            </button>
          ) : (
            <button
              className={`m-2 w-14 h-14 text-white flex justify-center items-center rounded-full bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker`}
              type="button"
              onClick={() => handleSearchClick()}
              aria-label="Search"
            >
              <IconSearch />
            </button>
          )}
        </div>
      </div>
      <div
        className="mt-8 flex w-80 items-center justify-between rounded-full bg-white py-2 pl-6 pr-2 shadow-lg lg:mt-12 lg:hidden"
        onClick={() => setShowSearchOverlay(true)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setShowSearchOverlay(true);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Open search"
      >
        <div>
          <div className="text-sm font-semibold text-neutral-800">
            {destination}
          </div>
          <div className="mt-1 text-xs text-neutral-500">
            {month} | {diver} divers
          </div>
        </div>
        <button
          className="m-2 flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker text-white"
          type="button"
          onClick={event => {
            event.stopPropagation();
            handleSearchClick();
          }}
          aria-label="Search"
        >
          <IconSearch size={16} />
        </button>
      </div>
    </div>
  );
};

export default MultiStageSearch;
